import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getHebergementById } from "../utils/api";

// ── Icônes ───────────────────────────────────────────────────
const IconPin = ({ size = 14, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>
);
const IconStar = ({ size = 16, filled = true }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "#F5A623" : "#E8DCC8"}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);
const IconArrow = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);
const IconCheck = ({ size = 16, color = "#3D6B2E" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconCalendar = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconUser = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconClock = ({ size = 18, color = "#C4441A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconPhone = ({ size = 15, color = "#7A5A3A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.95a16 16 0 0 0 6 6l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const IconMail = ({ size = 15, color = "#7A5A3A" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
);

// ── Carte Leaflet ─────────────────────────────────────────────
const HebergementMap = ({ lat, lng, nom }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (mapInstanceRef.current || !lat || !lng) return;
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css"; link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => {
      if (!mapRef.current || mapInstanceRef.current) return;
      const L = window.L;
      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false }).setView([lat, lng], 14);
      mapInstanceRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 18 }).addTo(map);
      const icon = L.divIcon({
        className: "",
        html: `<div style="background:#C4441A;color:white;padding:6px 12px;border-radius:20px;font-family:'Space Grotesk',sans-serif;font-size:12px;font-weight:600;white-space:nowrap;box-shadow:0 4px 12px rgba(196,68,26,0.4);border:2px solid white">🏠 ${nom}</div>`,
        iconAnchor: [60, 16],
      });
      L.marker([lat, lng], { icon }).addTo(map).bindPopup(`<b>${nom}</b>`).openPopup();
    };
    document.body.appendChild(script);
    return () => {};
  }, [lat, lng, nom]);

  return <div ref={mapRef} style={{ width: "100%", height: "100%" }} />;
};

// ── Composant principal ───────────────────────────────────────
export default function DetailHebergement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hebergement, setHebergement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [reservation, setReservation] = useState({ dateArrivee: "", dateDepart: "", voyageurs: 1 });
  const [nbNuits, setNbNuits] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getHebergementById(id);
        setHebergement(data);
      } catch (e) {
        navigate("/hebergements");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  useEffect(() => {
    if (reservation.dateArrivee && reservation.dateDepart) {
      const d1 = new Date(reservation.dateArrivee);
      const d2 = new Date(reservation.dateDepart);
      const diff = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
      setNbNuits(diff > 0 ? diff : 0);
    }
  }, [reservation.dateArrivee, reservation.dateDepart]);

  const formatPrix = (p) => p?.toLocaleString("fr-FR") + " FCFA";

  const Stars = ({ count }) => (
    <div style={{ display: "flex", gap: 3 }}>
      {[1,2,3,4,5].map(i => <IconStar key={i} size={16} filled={i <= count} />)}
    </div>
  );

  if (loading) return (
    <div style={{ fontFamily: "'Fraunces',serif", background: "#FAF5EC", minHeight: "100vh" }}>
      <Navbar transparent={false} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏠</div>
          <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 16, color: "#7A5A3A" }}>Chargement...</p>
        </div>
      </div>
    </div>
  );

  if (!hebergement) return null;

  const total = nbNuits * hebergement.prix;
  const typeLabel = hebergement.type === "villa" ? "Villa" : "Appartement";
  const typeEmoji = hebergement.type === "villa" ? "🏡" : "🏢";

  return (
    <div style={{ fontFamily: "'Fraunces',serif", background: "#FAF5EC", color: "#1C1008", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,700;0,9..144,800;1,9..144,400&family=Space+Grotesk:wght@400;500;600;700&display=swap');
        * { margin:0; padding:0; box-sizing:border-box; }

        .gallery-thumb {
          width: 80px; height: 60px; object-fit: cover; border-radius: 8px;
          cursor: pointer; transition: all .25s; border: 2px solid transparent; flex-shrink: 0;
        }
        .gallery-thumb:hover { transform: scale(1.05); }
        .gallery-thumb.active { border-color: #C4441A; }

        .equip-item {
          display: flex; align-items: center; gap: 8px;
          font-family: 'Space Grotesk', sans-serif; font-size: 14px; color: #1C1008;
          background: white; padding: 10px 14px; border-radius: 8px;
          border: 1.5px solid rgba(28,16,8,.06);
        }

        .date-input {
          width: 100%; padding: 12px 14px;
          border: 1.5px solid #E8DCC8; border-radius: 8px;
          font-family: 'Space Grotesk', sans-serif; font-size: 14px;
          color: #1C1008; background: white; outline: none; transition: all .25s;
        }
        .date-input:focus { border-color: #C4441A; box-shadow: 0 0 0 3px rgba(196,68,26,.08); }

        .btn-demander {
          width: 100%; padding: 15px; background: #C4441A; color: white;
          border: none; border-radius: 10px;
          font-family: 'Space Grotesk', sans-serif; font-size: 16px; font-weight: 700;
          cursor: pointer; transition: all .3s;
        }
        .btn-demander:hover { background: #A03614; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(196,68,26,.3); }
        .btn-demander:disabled { opacity: .5; cursor: not-allowed; transform: none; }

        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        .fade-up { animation: fadeUp .6s ease forwards; }

        @media(max-width:900px) {
          .detail-grid { grid-template-columns: 1fr !important; }
          .gallery-main { height: 280px !important; }
        }
        @media(max-width:767px) {
          .detail-content { padding: 32px 5% !important; }
        }
      `}</style>

      <Navbar transparent={false} />

      {/* ── BREADCRUMB ── */}
      <div style={{ paddingTop: 68, background: "white", borderBottom: "1px solid rgba(28,16,8,.07)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "14px 5%", display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => navigate("/")} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#7A5A3A" }}>Accueil</button>
          <span style={{ color: "#B8A898" }}>›</span>
          <button onClick={() => navigate("/hotels")} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#7A5A3A" }}>Hébergements</button>
          <span style={{ color: "#B8A898" }}>›</span>
          <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#1C1008", fontWeight: 600 }}>{hebergement.nom}</span>
        </div>
      </div>

      {/* ── GALERIE ── */}
      <section style={{ background: "#1C1008" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 5%" }}>
          <div className="gallery-main" style={{ height: 420, borderRadius: 16, overflow: "hidden", position: "relative", marginBottom: 12 }}>
            <img src={hebergement.images[activeImg] || hebergement.images[0]} alt={hebergement.nom} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(28,16,8,.6) 0%, transparent 50%)" }} />
            <div style={{ position: "absolute", top: 16, right: 16, background: "#C4441A", borderRadius: 8, padding: "6px 12px" }}>
              <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, fontWeight: 700, color: "white" }}>⭐ {hebergement.note} · {hebergement.avis} avis</span>
            </div>
            <div style={{ position: "absolute", bottom: 20, left: 24 }}>
              <Stars count={hebergement.etoiles || 3} />
            </div>
          </div>
          {hebergement.images.length > 1 && (
            <div style={{ display: "flex", gap: 8, overflowX: "auto" }}>
              {hebergement.images.map((img, i) => (
                <img key={i} src={img} alt={`photo ${i+1}`}
                  className={`gallery-thumb ${activeImg === i ? "active" : ""}`}
                  onClick={() => setActiveImg(i)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── CONTENU ── */}
      <section className="detail-content" style={{ padding: "48px 5% 80px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="detail-grid" style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 40, alignItems: "start" }}>

            {/* ── COLONNE GAUCHE ── */}
            <div>
              <div className="fade-up" style={{ marginBottom: 32 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: 2, color: "#C4441A", textTransform: "uppercase" }}>
                    {typeEmoji} {hebergement.categorie || typeLabel}
                  </span>
                </div>
                <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(28px,4vw,48px)", fontWeight: 800, letterSpacing: "-1px", lineHeight: 1.15, marginBottom: 12 }}>
                  {hebergement.nom}
                </h1>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <IconPin size={14} color="#7A5A3A" />
                  <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, color: "#7A5A3A" }}>{hebergement.adresse}</span>
                </div>
              </div>

              {/* Bannière processus de réservation */}
              <div style={{ marginBottom: 28, padding: "20px 24px", background: "rgba(196,68,26,.05)", borderRadius: 12, border: "1.5px solid rgba(196,68,26,.2)", display: "flex", gap: 14, alignItems: "flex-start" }}>
                <IconClock size={22} color="#C4441A" />
                <div>
                  <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700, color: "#C4441A", marginBottom: 4 }}>
                    Réservation sous confirmation
                  </p>
                  <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#7A5A3A", lineHeight: 1.6 }}>
                    Après votre demande, notre équipe vérifie la disponibilité et vous contacte sous 24h par téléphone ou email pour confirmer votre séjour.
                  </p>
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: 36, padding: "28px", background: "white", borderRadius: 14, border: "1.5px solid rgba(28,16,8,.06)" }}>
                <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 700, marginBottom: 14 }}>À propos</h2>
                <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, color: "#7A5A3A", lineHeight: 1.8 }}>
                  {hebergement.description}
                </p>
              </div>

              {/* Équipements */}
              <div style={{ marginBottom: 36 }}>
                <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 700, marginBottom: 18 }}>Équipements & Services</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                  {(hebergement.equipements || []).map((eq, i) => (
                    <div key={i} className="equip-item">
                      <IconCheck size={15} color="#3D6B2E" />
                      {eq}
                    </div>
                  ))}
                </div>
              </div>

              {/* Tarifs */}
              <div style={{ marginBottom: 36, padding: "24px 28px", background: "rgba(196,68,26,.05)", borderRadius: 14, border: "1.5px solid rgba(196,68,26,.15)" }}>
                <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Tarifs</h2>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontFamily: "'Fraunces',serif", fontSize: 36, fontWeight: 800, color: "#C4441A" }}>{formatPrix(hebergement.prix)}</span>
                  <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, color: "#7A5A3A" }}>/ nuit</span>
                </div>
                <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#7A5A3A", marginTop: 8 }}>
                  Taxes et frais inclus · Prix estimatif, confirmé après vérification
                </p>
              </div>

              {/* Carte */}
              <div style={{ marginBottom: 36 }}>
                <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 700, marginBottom: 18 }}>Localisation</h2>
                <div style={{ height: 320, borderRadius: 14, overflow: "hidden", border: "1.5px solid rgba(28,16,8,.08)", boxShadow: "0 8px 32px rgba(28,16,8,.1)" }}>
                  <HebergementMap lat={hebergement.lat} lng={hebergement.lng} nom={hebergement.nom} />
                </div>
                <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#7A5A3A", marginTop: 10, display: "flex", alignItems: "center", gap: 5 }}>
                  <IconPin size={12} color="#7A5A3A" />
                  {hebergement.adresse}
                </p>
              </div>
            </div>

            {/* ── COLONNE DROITE — Demande de réservation ── */}
            <div style={{ position: "sticky", top: 88 }}>
              <div style={{ background: "white", borderRadius: 16, padding: "28px", border: "1.5px solid rgba(28,16,8,.08)", boxShadow: "0 8px 40px rgba(28,16,8,.1)" }}>
                <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 800, marginBottom: 6 }}>
                  Demander à réserver
                </h3>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 20 }}>
                  <span style={{ fontFamily: "'Fraunces',serif", fontSize: 26, fontWeight: 800, color: "#C4441A" }}>{formatPrix(hebergement.prix)}</span>
                  <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#7A5A3A" }}>/ nuit</span>
                </div>

                {/* Alerte processus */}
                <div style={{ marginBottom: 20, padding: "12px 14px", background: "rgba(196,68,26,.05)", borderRadius: 8, border: "1px solid rgba(196,68,26,.15)", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <IconClock size={15} color="#C4441A" />
                  <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#C4441A", lineHeight: 1.5 }}>
                    La réservation sera confirmée après vérification de la disponibilité par notre équipe.
                  </p>
                </div>

                {/* Date arrivée */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, fontWeight: 700, color: "#1C1008", display: "flex", alignItems: "center", gap: 6, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
                    <IconCalendar size={14} /> Arrivée
                  </label>
                  <input type="date" className="date-input"
                    value={reservation.dateArrivee}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={e => setReservation({ ...reservation, dateArrivee: e.target.value })}
                  />
                </div>

                {/* Date départ */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, fontWeight: 700, color: "#1C1008", display: "flex", alignItems: "center", gap: 6, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
                    <IconCalendar size={14} /> Départ
                  </label>
                  <input type="date" className="date-input"
                    value={reservation.dateDepart}
                    min={reservation.dateArrivee || new Date().toISOString().split("T")[0]}
                    onChange={e => setReservation({ ...reservation, dateDepart: e.target.value })}
                  />
                </div>

                {/* Voyageurs */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, fontWeight: 700, color: "#1C1008", display: "flex", alignItems: "center", gap: 6, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
                    <IconUser size={14} /> Voyageurs
                  </label>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, border: "1.5px solid #E8DCC8", borderRadius: 8, padding: "8px 14px", background: "white" }}>
                    <button onClick={() => setReservation(r => ({ ...r, voyageurs: Math.max(1, r.voyageurs - 1) }))}
                      style={{ width: 28, height: 28, borderRadius: "50%", border: "1.5px solid #E8DCC8", background: "white", cursor: "pointer", fontSize: 16, fontWeight: 700, color: "#C4441A", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
                    <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, fontWeight: 600, flex: 1, textAlign: "center" }}>
                      {reservation.voyageurs} voyageur{reservation.voyageurs > 1 ? "s" : ""}
                    </span>
                    <button onClick={() => setReservation(r => ({ ...r, voyageurs: Math.min(20, r.voyageurs + 1) }))}
                      style={{ width: 28, height: 28, borderRadius: "50%", border: "1.5px solid #E8DCC8", background: "white", cursor: "pointer", fontSize: 16, fontWeight: 700, color: "#C4441A", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
                  </div>
                </div>

                {/* Récap estimatif */}
                {nbNuits > 0 && (
                  <div style={{ background: "#FAF5EC", borderRadius: 10, padding: "16px", marginBottom: 20, border: "1px solid rgba(196,68,26,.1)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, color: "#7A5A3A" }}>
                        {formatPrix(hebergement.prix)} × {nbNuits} nuit{nbNuits > 1 ? "s" : ""}
                      </span>
                      <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, color: "#1C1008", fontWeight: 600 }}>
                        {formatPrix(total)}
                      </span>
                    </div>
                    <div style={{ borderTop: "1px solid rgba(28,16,8,.08)", paddingTop: 10, display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontFamily: "'Fraunces',serif", fontSize: 15, fontWeight: 700, color: "#7A5A3A" }}>Total estimé</span>
                      <span style={{ fontFamily: "'Fraunces',serif", fontSize: 17, fontWeight: 800, color: "#C4441A" }}>{formatPrix(total)}</span>
                    </div>
                    <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 11, color: "#B8A898", marginTop: 8 }}>
                      * Montant définitif confirmé après vérification
                    </p>
                  </div>
                )}

                <button className="btn-demander"
                  disabled={nbNuits === 0}
                 onClick={() => navigate(`/demande-reservation/${hebergement.id}`, { 
  state: { hebergement, reservation, nbNuits, total } 
})}
                >
                  {nbNuits === 0 ? "Choisir les dates" : "Envoyer ma demande →"}
                </button>

                {/* Info contact */}
                <div style={{ marginTop: 16, padding: "14px", background: "#FAF5EC", borderRadius: 8, border: "1px solid rgba(28,16,8,.08)" }}>
                  <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, fontWeight: 700, color: "#1C1008", marginBottom: 10 }}>
                    Nous vous contacterons par :
                  </p>
                  <div style={{ display: "flex", gap: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <IconMail size={13} color="#7A5A3A" />
                      <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#7A5A3A" }}>Email</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <IconPhone size={13} color="#7A5A3A" />
                      <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#7A5A3A" }}>Téléphone</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Retour */}
              <button onClick={() => navigate("/hebergements")}
                style={{ width: "100%", marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "none", border: "1.5px solid rgba(28,16,8,.15)", borderRadius: 10, padding: "12px", cursor: "pointer", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 600, color: "#7A5A3A", transition: "all .25s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "#C4441A"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(28,16,8,.15)"}
              >
                <IconArrow size={16} /> Voir tous les hébergements
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}